import { ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '@components/global/Layout';
import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import getBannerStripGlobalData from '@adapters/prismic/fetching/banner-strip-global/getBannerStripGlobalData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';
import type {
  GetStaticPropsContext,
  GetStaticPathsResult,
  GetStaticPropsResult,
} from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';
import { altLocale } from '@utils/locale';
import { localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import getCategoryPageByUid from '@adapters/prismic/fetching/categoryPage/getCategoryPageByUid';
import CategoryPageSliceZone from '@components/cms/CategoryPageSliceZone';
import { handlePromoDetailsTracking } from '@utils/gtm';
import {
  AlgonomyPlacement,
  RecommendationsLoader,
} from '@components/product/ProductCollectionComponent';
import { CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary } from 'prismicio-types';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';
import {
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
  FEATURE_FLAG_IMAGE_HOTSPOT,
} from '@config/feature-flag';
import {
  getAlgonomyPlacementCategories,
  logAlgonomyPlacementCategoryConflict,
} from '@adapters/prismic/utils/getAlgonomyPlacementCategory';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';
import { collectImageHotspotSkus } from '@adapters/prismic/transforms/imageHotspot';
import { getProductsByProductCodes } from '@adapters/algolia/getProductsByProductCodes';
import type { HitProps } from '@components/algolia/Hit';

interface CategoryPageProps {
  categoryPage: any;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  stores: MyStoreData[] | null;
  recommendationsCategory: AlgonomyPlacement | null;
  imageHotspotProducts: Record<string, HitProps>;
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext<{ locale: string; slug: string }>): Promise<
  GetStaticPropsResult<CategoryPageProps>
> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  const defaultLocale = 'en';
  if (!params?.slug) {
    throw new Error(`Branded Store page has no slug`);
  }

  if (!locale) {
    throw new Error('No locale specified for page');
  }

  const pageLocale = locale ?? defaultLocale;
  const previewRef = getPreviewRef(preview, previewData);

  const categoryPage = await getCategoryPageByUid(
    params.slug,
    pageLocale,
    previewRef
  );

  if (!categoryPage) {
    throw new Error(`Category page with slug '${params?.slug}' not found`);
  }

  const [
    stores,
    promoMessages,
    bannerStripData,
    serverSideTranslation,
    promoBarData,
  ] = await Promise.allSettled([
    getAllStoresDataForSetMyStore(locale),
    !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
      locale &&
      getAllPromoTickerData(locale),
    getBannerStripGlobalData(locale, preview ? previewRef : undefined),
    // 'plp' namespace is required by QuickAdd (rendered inside a
    // category_page's image_hotspot slice's ProductTile) — without it,
    // t('quickAdd.cta') etc. render as their raw i18n keys.
    serverSideTranslations(locale, ['common', 'plp']),
    FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
  ]).then((results) =>
    results.map((promise) => {
      if (
        promise.status === 'rejected' ||
        typeof promise.value === 'undefined'
      ) {
        console.error('promise rejected or returning undefined', promise);
        return null;
      }
      return promise.value;
    })
  );

  const canonicalUrl = localePathAbs(locale, `/category/${params.slug}`);
  const langAltUrl = localeAltPathAbs(locale, `/category/${params.slug}`);
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  // When present, an algonomy_placement's category takes over the page's
  // request instead of falling back to best_seller_recommendations, so this
  // slice never silently depends on another slice's data.
  const algonomyPlacementCategories = getAlgonomyPlacementCategories(
    categoryPage.body
  );
  logAlgonomyPlacementCategoryConflict(
    'Category page',
    params.slug,
    algonomyPlacementCategories
  );

  const recommendationsCategory =
    algonomyPlacementCategories[0] ??
    (
      categoryPage.body.find(
        (slice: { slice_type: string }) =>
          slice.slice_type === 'best_seller_recommendations'
      )
        ?.primary as CategoryPageDocumentDataBodyBestSellerRecommendationsSlicePrimary
    )?.category ??
    null;

  // ED-345: resolve every image_hotspot slice's authored SKUs to live product
  // data in one batched Algolia call, same pattern as the PLP deals strip
  // (adapters/prismic/transforms/plpProductStripDeals.ts). Skipped entirely
  // when the flag is off (the slice handler renders nothing then, so this
  // would otherwise be a wasted Algolia call on every build/ISR revalidation)
  // or when the page has no image_hotspot slice.
  const imageHotspotSkus = FEATURE_FLAG_IMAGE_HOTSPOT
    ? categoryPage.body
        .filter(
          (slice: { slice_type: string }) =>
            slice.slice_type === 'image_hotspot'
        )
        .flatMap((slice: any) => collectImageHotspotSkus(slice))
    : [];
  const imageHotspotProducts: Record<string, HitProps> =
    imageHotspotSkus.length > 0
      ? Object.fromEntries(
          await getProductsByProductCodes(imageHotspotSkus, locale)
        )
      : {};

  return {
    props: {
      stores,
      categoryPage,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      bannerStripData,
      recommendationsCategory,
      promoBarData,
      imageHotspotProducts,
    },
  };
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export default function CategoryPageComponent({
  categoryPage,
  canonicalUrl,
  langAltUrl,
  xDefault,
  recommendationsCategory,
  imageHotspotProducts,
}: CategoryPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { meta_title: metaTitle, meta_description: metaDescription } =
    categoryPage;

  const [categoryPageRecommendations, setCategoryPageRecommendations] =
    useState<AlgonomyPlacement[]>([]);

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        description={metaDescription}
        title={`${metaTitle} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          locale,
        }}
      />

      <RecommendationsLoader
        pageType="categoryLandingPage"
        data={{
          category: recommendationsCategory,
        }}
        onLoad={(data) => {
          setCategoryPageRecommendations(data);
        }}
        trigger={recommendationsCategory !== undefined}
      />

      <CategoryPageSliceZone
        slices={categoryPage?.body}
        categoryPageRecommendations={categoryPageRecommendations}
        imageHotspotProducts={imageHotspotProducts}
      />
    </>
  );
}

CategoryPageComponent.Layout = Layout;
